import React, { useEffect, useRef, useState, useCallback } from "react";
import { Send, Paperclip, X, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import { newIdempotencyKey, AuthExpiredError, TurfTalk, uploadMedia, mediaContentUrl } from "@/api/client";
import { CHAR_COLOR } from "@/lib/tokens";
import { useAuth } from "@/context/AuthContext";
import { trackMessageSent } from "@/lib/telemetry";
import { dedupeAndOrder, maxSeqOf, isNearBottom } from "@/lib/roomMessages";
import { runRoomAsk, agentDisplayName, mentionNote } from "@/lib/roomAsk";
import { startAwaiting, isAwaitingActive, REPLY_WAIT } from "@/lib/roomReply";
import { humanizeTimestamps } from "@/lib/datetime";

const ACCEPT = "image/jpeg,image/png,image/gif,image/webp,video/mp4";
const IMAGE_MAX = 25 * 1024 * 1024;   // client pre-check; backend is authoritative
const VIDEO_MAX = 200 * 1024 * 1024;

// Per-role accent for clearly-labeled AI identities (backend owns the roles/logic).
const AGENT_ACCENT = { host: "#3D8BFD", analyst: "#8B5CF6", predictor: "#F4B740" };
const agentAccent = (role) => AGENT_ACCENT[role] || "#8B5CF6";

function pickKind(file) {
  if (file.type === "video/mp4") return "video";
  if (["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)) return "image";
  return null;
}

// Renders a message's recorded media via the signed, access-rechecked content
// route. Fetches fresh metadata (mints a short-lived token) per media id.
function MessageMedia({ mediaId }) {
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    TurfTalk.mediaMeta(mediaId)
      .then(setMeta)
      .catch((e) => { if (!(e instanceof AuthExpiredError)) setErr(e.message || "Media unavailable"); });
  }, [mediaId]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="mt-1 text-[11px] text-[#ff8a80] flex items-center gap-1" data-testid="msg-media-error"><AlertTriangle size={12} /> {err} <button onClick={load} className="underline">retry</button></div>;
  if (!meta) return <div className="mt-1 h-24 rounded-lg bg-black/20 grid place-items-center text-white/30 text-xs"><Loader2 className="animate-spin" size={16} /></div>;
  if (meta.state === "processing" || meta.state === "uploading") return <div className="mt-1 text-[11px] text-white/50" data-testid="msg-media-processing">Processing media…</div>;
  if (meta.state !== "ready" || !meta.content_url) return <div className="mt-1 text-[11px] text-white/40" data-testid="msg-media-unavailable">Media unavailable.</div>;
  const url = mediaContentUrl(meta.content_url);
  if (meta.kind === "video") {
    return <video data-testid="msg-media-video" src={url} controls playsInline preload="metadata" onError={load} className="mt-1 rounded-lg max-w-full w-full max-h-[360px] bg-black" />;
  }
  return <img data-testid="msg-media-image" src={url} alt="attachment" onError={load} loading="lazy" className="mt-1 rounded-lg max-w-full max-h-[360px] object-contain bg-black/20" />;
}

export function Chat({ loadFn, sendFn, title, height = 460, contextType = "upload", contextRefId = null, roomId = null, refreshSignal = 0 }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [att, setAtt] = useState(null); // { name, kind, status: uploading|ready|error, progress, media_id, error }
  const [ai, setAi] = useState(null);          // ai-status payload (null until first fetch / null if unsupported)
  const [asking, setAsking] = useState(null);  // role currently being requested (shows typing indicator)
  const [selectedRole, setSelectedRole] = useState(null); // explicit chip selection (stays visually selected)
  const [askError, setAskError] = useState(null); // { role } → offer a clean Retry
  const [awaiting, setAwaiting] = useState(null);  // working state after a human send (backend reply pending)
  const [aiNote, setAiNote] = useState("");    // transient copy for cooldown/quiet/kill
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const askAbortRef = useRef(null);
  const askTokenRef = useRef(0);
  const askKeyRef = useRef(null);
  const awaitingRef = useRef(null);
  const seenSeq = useRef(-1);

  const scrollToBottom = () => {
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
  };

  const refresh = useCallback(async () => {
    try {
      const r = await loadFn({ limit: 60 });
      const msgs = dedupeAndOrder(r.messages || r.items || []);
      // Stick to bottom only when new content arrived AND the reader is already
      // following the live feed — otherwise preserve their scroll position.
      const stick = scrollRef.current ? isNearBottom(scrollRef.current) : true;
      setMessages(msgs);
      const maxSeq = maxSeqOf(msgs);
      if (maxSeq > seenSeq.current) { seenSeq.current = maxSeq; if (stick) scrollToBottom(); }
      // Clear the human-send working state once a new agent reply arrived (or it timed out).
      if (awaitingRef.current && !isAwaitingActive(awaitingRef.current, msgs)) { awaitingRef.current = null; setAwaiting(null); }
    } catch (e) {
      if (!(e instanceof AuthExpiredError)) { /* keep silent on poll */ }
    }
  }, [loadFn]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  // External nudge (e.g. after a "Discuss" headline is posted from the News
  // panel) so the new message appears immediately instead of on the next poll.
  useEffect(() => { if (refreshSignal) refresh(); }, [refreshSignal, refresh]);

  // AI engagement status (rooms only). Never invents support — shows the bar
  // ONLY when the shared backend reports agents enabled and the kill switch off.
  const refreshAiStatus = useCallback(() => {
    if (!roomId) return;
    TurfTalk.roomAiStatus(roomId)
      .then(setAi)
      .catch((e) => { if (!(e instanceof AuthExpiredError)) setAi(null); });
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return;
    refreshAiStatus();
    const t = setInterval(refreshAiStatus, 20000);
    return () => clearInterval(t);
  }, [roomId, refreshAiStatus]);

  // Direct ask. The backend routes to the chosen agent and bypasses ambient
  // cooldown, so we NEVER locally suppress a direct question. Selection stays
  // visible, a typing indicator shows immediately, and timeout/error offer a
  // clean Retry that reuses the idempotency key (no duplicated work).
  const askAgent = useCallback(async (role, { isRetry } = {}) => {
    if (!roomId || (asking && !isRetry)) return;
    setSelectedRole(role);
    setAskError(null);
    setAiNote("");
    if (!isRetry || !askKeyRef.current) askKeyRef.current = newIdempotencyKey();
    const token = ++askTokenRef.current;
    const controller = new AbortController();
    askAbortRef.current = controller;
    setAsking(role);
    scrollToBottom();

    const out = await runRoomAsk({
      signal: controller.signal,
      ask: () => TurfTalk.roomAiMention(roomId, role, askKeyRef.current),
    });

    if (token !== askTokenRef.current) return; // stale (superseded or unmounted)
    setAsking(null);
    if (out.status === "cancelled") return;
    if (out.status === "timeout") { setAskError({ role }); setAiNote("The agent is taking longer than usual."); return; }
    if (out.status === "error") {
      if (out.error instanceof AuthExpiredError) return;
      setAskError({ role }); setAiNote(out.error?.message || "Agents are unavailable right now.");
      return;
    }
    const resp = out.response;
    if (!resp.acted) setAiNote(resp.note || mentionNote(resp.reason));
    askKeyRef.current = null; // consumed; next ask gets a fresh key
    await refresh();
    scrollToBottom();
    refreshAiStatus();
    // One optional delayed second-agent reaction — server-driven only.
    if (resp.expectSecondAgent) {
      const t = token;
      setTimeout(() => { if (t === askTokenRef.current) refresh(); }, resp.secondAgentDelayMs);
    }
  }, [roomId, asking, refresh, refreshAiStatus]);

  // Cancel any in-flight ask on unmount / navigation.
  useEffect(() => () => { askTokenRef.current += 1; if (askAbortRef.current) askAbortRef.current.abort(); }, []);

  const agentsOn = !!(ai && ai.agents_enabled && !ai.kill_switch && Array.isArray(ai.roles) && ai.roles.length);

  const roleDisplay = (role) => agentDisplayName(role, ai?.roles);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const kind = pickKind(file);
    if (!kind) { setError("Unsupported file. Use JPG, PNG, GIF, WEBP images or MP4 video."); return; }
    if (kind === "image" && file.size > IMAGE_MAX) { setError("Image is too large (max 25 MB)."); return; }
    if (kind === "video" && file.size > VIDEO_MAX) { setError("Video is too large (max 200 MB)."); return; }
    setError("");
    const controller = new AbortController();
    abortRef.current = controller;
    setAtt({ name: file.name, kind, status: "uploading", progress: 0 });
    try {
      const done = await uploadMedia({
        file, kind, contextType, contextRefId,
        onProgress: (p) => setAtt((a) => (a ? { ...a, progress: p } : a)),
        signal: controller.signal,
      });
      if (done.state === "failed" || done.state === "quarantined") throw new Error(done.error_reason || "Upload failed.");
      setAtt((a) => (a ? { ...a, status: "ready", media_id: done.id, progress: 1 } : a));
    } catch (err) {
      if (err?.name === "AbortError") { setAtt(null); return; }
      setAtt((a) => (a ? { ...a, status: "error", error: err.message || "Upload failed." } : a));
    } finally {
      abortRef.current = null;
    }
  };

  const cancelAtt = async () => {
    if (abortRef.current) abortRef.current.abort();
    if (att?.media_id) { try { await TurfTalk.mediaDelete(att.media_id); } catch (_) {} }
    setAtt(null);
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    // Never send while an attachment is still uploading, and never reference an incomplete upload.
    if (att && att.status !== "ready") { setError(att.status === "error" ? "Attachment failed — remove it or retry."  : "Attachment still uploading…"); return; }
    const mediaId = att?.status === "ready" ? att.media_id : null;
    if ((!body && !mediaId) || sending) return;
    setSending(true); setError("");
    try {
      await sendFn({ body, media_id: mediaId, idempotency_key: newIdempotencyKey() });
      if (contextType === "room") trackMessageSent(contextRefId || roomId);
      setText(""); setAtt(null);
      // Enter the working lifecycle immediately for a genuine human message in a
      // room with agents — NEVER gated on ambient cooldown (backend owns that).
      const agentsEnabled = !!(ai && ai.agents_enabled && !ai.kill_switch && Array.isArray(ai.roles) && ai.roles.length);
      const a = startAwaiting({ roomId, agentsEnabled, maxSeq: seenSeq.current });
      if (a) { awaitingRef.current = a; setAwaiting(a); }
      await refresh();
      scrollToBottom();
      // Responsive nudges so a reply appears fast without waiting on the 4s poll.
      if (a) { setTimeout(() => refresh(), REPLY_WAIT.nudgeMs); setTimeout(() => refresh(), REPLY_WAIT.nudge2Ms); }
    } catch (err) {
      setError(err.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="tt-panel rounded-xl flex flex-col" style={{ height }} data-testid="chat">
      {title && <div className="px-4 h-12 flex items-center border-b border-[#2A3544] font-display tracking-wide text-white text-sm shrink-0">{title}</div>}
      <div ref={scrollRef} className="flex-1 overflow-y-auto tt-scroll p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="h-full grid place-items-center text-white/40 text-sm">No messages yet. Start the conversation.</div>
        ) : messages.map((m) => {
          const mine = m.author_id === user?.id && !m.is_ai;
          const name = m.author?.display_name || m.author?.handle || (m.is_character ? m.author_id : "Member");
          if (m.is_system) return <div key={m.id} className="text-center text-[11px] text-white/40">{m.body}</div>;
          if (m.is_ai) {
            const accent = agentAccent(m.agent_role);
            const stale = m.data_state && m.data_state !== "fresh";
            return (
              <div key={m.id} className="flex justify-start" data-testid={`chat-msg-${m.id}`} data-ai="true">
                <div className="max-w-[82%] rounded-2xl px-3.5 py-2 bg-[#141C28] text-white border" style={{ borderColor: `${accent}66` }}>
                  <div className="text-[11px] mb-0.5 font-medium flex items-center gap-1.5" style={{ color: accent }} data-testid={`chat-ai-label-${m.id}`}>
                    <Sparkles size={11} />
                    {m.author?.display_name || roleDisplay(m.agent_role)}
                    <span className="text-[9px] px-1 py-px rounded bg-white/10 text-white/70 tracking-wide">AI</span>
                  </div>
                  {m.removed ? <div className="text-sm"><em className="opacity-60">message removed</em></div> : (
                    <div className="text-sm leading-relaxed whitespace-pre-wrap break-words" data-testid={`chat-ai-body-${m.id}`}>{humanizeTimestamps(m.body)}</div>
                  )}
                  {stale && <div className="mt-1 text-[10px] text-white/35">Based on the latest data available.</div>}
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? "bg-[#2F7D32] text-white" : "bg-[#1E2A3B] text-white"}`}>
                {!mine && (
                  <div className="text-[11px] mb-0.5 font-medium" style={{ color: m.is_character ? CHAR_COLOR[m.author_id] : "#8aa0b8" }}>
                    {m.author?.handle ? `@${m.author.handle}` : name}{m.is_character ? " · CHARACTER" : ""}
                  </div>
                )}
                {m.removed ? <div className="text-sm"><em className="opacity-60">message removed</em></div> : (
                  <>
                    {m.body && <div className="text-sm leading-snug break-words">{m.body}</div>}
                    {m.media_id && <MessageMedia mediaId={m.media_id} />}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {asking && (
          <div className="flex justify-start" data-testid="chat-ai-typing">
            <div className="max-w-[82%] rounded-2xl px-3.5 py-2 bg-[#141C28] text-white border" style={{ borderColor: `${agentAccent(asking)}66` }}>
              <div className="text-[11px] mb-1 font-medium flex items-center gap-1.5" style={{ color: agentAccent(asking) }}>
                <Sparkles size={11} />
                {roleDisplay(asking)}
                <span className="text-[9px] px-1 py-px rounded bg-white/10 text-white/70 tracking-wide">AI</span>
              </div>
              <div className="flex items-center gap-1 py-0.5" aria-label="typing">
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        {!asking && awaiting && (
          <div className="flex justify-start" data-testid="chat-room-working">
            <div className="max-w-[82%] rounded-2xl px-3.5 py-2 bg-[#141C28] text-white border border-[#8B5CF666]">
              <div className="text-[11px] mb-1 font-medium flex items-center gap-1.5 text-[#8B5CF6]">
                <Sparkles size={11} />
                Turf Talk
                <span className="text-[9px] px-1 py-px rounded bg-white/10 text-white/70 tracking-wide">AI</span>
              </div>
              <div className="flex items-center gap-1 py-0.5" aria-label="working">
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {agentsOn && (
        <div className="px-3 pt-2 shrink-0" data-testid="chat-ai-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-white/40 inline-flex items-center gap-1"><Sparkles size={12} className="text-[#8B5CF6]" /> Ask an agent:</span>
            {ai.roles.map((r) => {
              const isSel = selectedRole === r.role;
              const isBusy = asking === r.role;
              const accent = agentAccent(r.role);
              return (
                <button
                  key={r.role}
                  type="button"
                  onClick={() => askAgent(r.role)}
                  disabled={!!asking}
                  aria-pressed={isSel}
                  data-selected={isSel ? "true" : "false"}
                  data-testid={`chat-ai-mention-${r.role}`}
                  className="px-2.5 h-7 rounded-full text-[11px] font-medium border transition-colors disabled:opacity-40 hover:bg-white/5 inline-flex items-center gap-1"
                  style={isSel
                    ? { borderColor: accent, backgroundColor: `${accent}22`, color: accent }
                    : { borderColor: `${accent}66`, color: accent }}
                >
                  {isBusy && <Loader2 size={11} className="animate-spin" />}
                  {r.display_name || r.role}
                </button>
              );
            })}
          </div>
          {aiNote && (
            <div className="mt-1 text-[10px] text-white/45 flex items-center gap-2" data-testid="chat-ai-note">
              <span>{aiNote}</span>
              {askError && (
                <button type="button" onClick={() => askAgent(askError.role, { isRetry: true })} disabled={!!asking} data-testid="chat-ai-retry" className="underline text-white/70 hover:text-white disabled:opacity-40">Retry</button>
              )}
            </div>
          )}
        </div>
      )}

      {att && (
        <div className="px-3 pt-2 shrink-0" data-testid="chat-attachment">
          <div className="flex items-center gap-2 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 py-2 text-xs text-white/80">
            {att.status === "uploading" && <Loader2 size={14} className="animate-spin" />}
            <span className="truncate flex-1">{att.name} <span className="text-white/40">· {att.kind}</span></span>
            {att.status === "uploading" && <span className="tabnums text-white/50" data-testid="chat-att-progress">{Math.round((att.progress || 0) * 100)}%</span>}
            {att.status === "ready" && <span className="text-[#4ea152]" data-testid="chat-att-ready">ready</span>}
            {att.status === "error" && <span className="text-[#ff8a80]" data-testid="chat-att-error">{att.error}</span>}
            <button type="button" onClick={cancelAtt} data-testid="chat-att-remove" className="text-white/50 hover:text-white"><X size={14} /></button>
          </div>
        </div>
      )}

      <form onSubmit={send} className="p-3 border-t border-[#2A3544] flex items-center gap-2 shrink-0">
        <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPick} className="hidden" data-testid="chat-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={!!att} data-testid="chat-attach-btn" className="w-10 h-10 grid place-items-center rounded-lg tt-panel text-white/70 hover:text-white disabled:opacity-40 transition-colors" title="Attach photo or video"><Paperclip size={16} /></button>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Say something…"
          data-testid="chat-input"
          className="flex-1 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 h-10 text-sm text-white placeholder:text-white/30 focus:border-[#2F7D32] outline-none"
        />
        <button type="submit" disabled={sending || (att && att.status !== "ready") || (!text.trim() && !(att && att.status === "ready"))} data-testid="chat-send" className="w-10 h-10 grid place-items-center rounded-lg bg-[#2F7D32] text-white disabled:opacity-40 hover:bg-[#276b2a] transition-colors">
          <Send size={16} />
        </button>
      </form>
      {error && <div className="px-3 pb-2 text-[11px] text-[#ff6b60]" data-testid="chat-error">{error}</div>}
    </div>
  );
}
